import { DocumentNode, useQuery } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { useMemo, useState } from "react";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FormFieldRoleAssignment from "components/FormFields/FormFieldRoleAssignment";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import ListBoxItem from "ds/components/ListBox/Item";
import SelectNew from "ds/components/Select/New";
import Textarea from "ds/components/Textarea";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useTypedContext from "hooks/useTypedContext";
import { ApiKeyType, BillingTier, BillingTierFeature } from "types/generated";
import { stringIsRequired, validateRegExp, validateURL } from "utils/formValidators";
import { getDocsUrl } from "utils/getDocsUrl";
import useTierFeature from "views/Account/hooks/useTierFeature";
import { getManagementStrategy } from "views/Account/Settings/helpers";
import { UserManagementActivationStatus } from "views/Account/Settings/types";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";

import CustomListBoxItem from "./CustomListBoxItem";
import { USER_ROLES_FOR_API_KEY_CREATION } from "./gql";
import { API_KEY_CREATE_DRAWER_TEST_ID } from "./constants";
import FormFieldApiKeyGroups from "../components/FormFieldApiKeyGroups";
import { CreateApiKeyFields } from "./types";
import useCreateApiKey from "./useCreateApiKey";
import { SPACE_ADMIN_ROLE_SLUG } from "../../constants";

type ApiKeysCreateDrawerProps = {
  activationStatus: UserManagementActivationStatus;
  refetchQueries?: DocumentNode[];
  analyticsPage: AnalyticsPageOrganization;
};

const ApiKeysCreateDrawer = createDrawer(
  ({ activationStatus, refetchQueries, analyticsPage }: ApiKeysCreateDrawerProps) => {
    const { tier } = useTypedContext(SubscriptionContext);

    const drawer = useModal();

    const isOidcApiKeyFeatureActive = useTierFeature(BillingTierFeature.ApiKeysOidc);

    const isWarningShown =
      tier === BillingTier.Cloud || tier === BillingTier.V3Cloud || tier === BillingTier.V4Starter;

    const trackSegmentAnalyticsEvent = useAnalytics({
      page: analyticsPage,
      defaultCallbackTrackProperties: {
        managementStrategy: getManagementStrategy(activationStatus),
      },
    });

    const { data, loading: rolesLoading } = useQuery(USER_ROLES_FOR_API_KEY_CREATION);

    const availableRoles = useMemo(() => data?.roles || [], [data?.roles]);

    const spaceAdminRole = useMemo(() => {
      return availableRoles.find((role) => role.slug === SPACE_ADMIN_ROLE_SLUG && role.isSystem);
    }, [availableRoles]);

    const [assignRolesFormHasDirtyFields, setAssignRolesFormHasDirtyFields] = useState(false);

    const groupAddForm = useForm<CreateApiKeyFields>({
      defaultValues: {
        description: "",
        name: "",
        groups: [],
        type: ApiKeyType.Secret,
        oidcClientId: "",
        oidcIssuer: "",
        oidcSubjectExpression: ".*",
        assignedRoles: [],
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      watch,
      formState: { errors, isDirty },
    } = groupAddForm;

    const handleCloseDrawer = () => {
      drawer.hide();
    };

    const { onCreate } = useCreateApiKey({
      refetchQueries,
      callback: handleCloseDrawer,
    });

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    const onSubmit: SubmitHandler<CreateApiKeyFields> = (formFields) => {
      trackSegmentAnalyticsEvent("Create Confirm");
      const input = {
        admin: false, // TODO: remove when legacy field is removed
        accessRules: [],
        machine: null,
        name: formFields.name.trim(),
        teams: formFields.groups?.map((group) => group.value) || [],
        type: formFields.type,
        oidc:
          formFields.type === ApiKeyType.Oidc
            ? {
                clientId: formFields.oidcClientId,
                issuer: formFields.oidcIssuer,
                subjectExpression: formFields.oidcSubjectExpression.trim(),
              }
            : null,
        description: formFields.description.trim(),
        roleBindings: formFields.assignedRoles,
      };
      onCreate(input);
    };

    const isUserManagementActive = activationStatus === UserManagementActivationStatus.ACTIVE;

    const type = watch("type");

    return (
      <DrawerForm
        isDirty={isDirty || assignRolesFormHasDirtyFields}
        dataTestId={API_KEY_CREATE_DRAWER_TEST_ID}
      >
        <FormProvider {...groupAddForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Create API key" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody fullHeight hasStickyFooter>
            {isWarningShown && (
              <Box margin="large 0 0 0">
                <Banner variant="warning">
                  <Typography tag="p" variant="p-body3" margin="0 0 medium 0">
                    API keys are virtual users and are billed like regular users, too. Thus, each
                    API key used (exchanged for a token) during any given billing cycle counts
                    against the total number of users.
                  </Typography>
                  {/** TODO: update docs link once we have updated documentation */}
                  <ReadMoreDocsLink
                    docsUrl={getDocsUrl("/integrations/api#spacelift-api-key-token")}
                  />
                </Banner>
              </Box>
            )}

            <FormField label="Name" error={errors?.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors?.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <Controller
              name="type"
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error?.message} label="Type" fullWidth>
                  {({ ariaInputProps }) => (
                    <Box direction="column" gap="x-large">
                      <SelectNew
                        placeholder="Type in or select type from the list"
                        value={field.value}
                        items={[
                          { label: "Secret", value: ApiKeyType.Secret },
                          {
                            label: "OIDC",
                            value: ApiKeyType.Oidc,
                            disabled: !isOidcApiKeyFeatureActive,
                            tooltip: isOidcApiKeyFeatureActive
                              ? undefined
                              : "Upgrade your plan to benefit from ability to use more secure OIDC-based API keys.",
                          },
                        ]}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        {...ariaInputProps}
                      >
                        {(itemProps) => (
                          <ListBoxItem
                            id={itemProps.value}
                            key={itemProps.label}
                            textValue={itemProps.label}
                            isDisabled={itemProps.disabled}
                          >
                            <CustomListBoxItem {...itemProps} />
                          </ListBoxItem>
                        )}
                      </SelectNew>
                    </Box>
                  )}
                </FormField>
              )}
            />

            {type === ApiKeyType.Secret && (
              <Box margin="large 0 0 0">
                <Banner variant="info">
                  Upon API key creation, a file will be generated with API key secret token details.
                  Please save this file somewhere safe as it will never be available again.
                </Banner>
              </Box>
            )}

            {type === ApiKeyType.Oidc && (
              <>
                <FormField label="Issuer" error={errors?.oidcIssuer?.message}>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="https://"
                      error={!!errors?.oidcIssuer}
                      {...register("oidcIssuer", {
                        required: "Issuer field is required.",
                        validate: validateURL({ https: true }),
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
                <FormField label="Client ID (audience)" error={errors?.oidcClientId?.message}>
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Enter Client ID (audience)"
                      error={!!errors?.oidcClientId}
                      {...register("oidcClientId", {
                        required: "Client ID (audience) field is required.",
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
                <FormField
                  label="Subject expression"
                  error={errors?.oidcSubjectExpression?.message}
                >
                  {({ ariaInputProps }) => (
                    <Input
                      placeholder="Enter subject expression"
                      error={!!errors?.oidcSubjectExpression}
                      {...register("oidcSubjectExpression", {
                        required: "Subject expression field is required.",
                        validate: validateRegExp(),
                      })}
                      {...ariaInputProps}
                    />
                  )}
                </FormField>
              </>
            )}

            <FormFieldApiKeyGroups name="groups" activationStatus={activationStatus} isOptional />

            <Box margin="large 0 0 0" direction="column">
              {isUserManagementActive ? (
                <FormFieldRoleAssignment
                  name="assignedRoles"
                  availableRoles={availableRoles}
                  loading={rolesLoading}
                  setHasDirtyFields={setAssignRolesFormHasDirtyFields}
                />
              ) : (
                <Controller
                  name="assignedRoles"
                  render={({ field }) => (
                    <FormToggleField
                      variant="checkbox"
                      title="Root Admin Access (Bypass Login Policy)"
                      description="Grants unrestricted access to the platform — even if login policies are broken or misconfigured. Use only as a last resort. This key bypasses all access controls and should be stored securely offline."
                      checked={field.value.length > 0}
                      onChange={(checked) => {
                        if (checked && spaceAdminRole) {
                          field.onChange([{ spaceID: "root", roleID: spaceAdminRole.id }]);
                        } else {
                          field.onChange([]);
                        }
                      }}
                      disabled={rolesLoading || !spaceAdminRole}
                      titleSuffix={
                        <Typography tag="span" variant="p-body3" color="secondary">
                          (Optional)
                        </Typography>
                      }
                    />
                  )}
                />
              )}
            </Box>

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <DrawerFooter sticky>
              <DrawerFooterActions>
                <Button
                  variant="secondary"
                  analyticsPage={analyticsPage}
                  analyticsTitle="Cancel Create Key"
                  analyticsProps={{ managementStrategy: getManagementStrategy(activationStatus) }}
                  onPress={handleCloseDrawer}
                >
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick}>
                  Create
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showApiKeysCreateDrawer = createDrawerTrigger(ApiKeysCreateDrawer);
